import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AppBranding from './AppBranding';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Separator } from './ui/separator';
import {
  ArrowLeft, Crown, Shield, User, Users, Zap, TrendingUp, TrendingDown,
  Target, Trophy, Calendar, Clock, Activity, BarChart3, Settings, Flame,
  Award, AlertTriangle, CheckCircle, XCircle, Loader2, DollarSign,
  Download, Mail, Gem, Cpu, Banknote, Scissors, ArrowDownCircle,
  ShoppingBag, Skull, ThumbsDown, Flag, Frown, Anchor, Factory,
  Moon, Sunrise, Heart, Layers, Repeat, Dog
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_V2 = process.env.REACT_APP_BACKEND_URL + '/api/v2';

const TIER_STYLES = {
  GOD: { bg: 'bg-red-900/40', border: 'border-red-600', text: 'text-red-300', icon: <Crown className="h-4 w-4" /> },
  FAMILY: { bg: 'bg-purple-900/40', border: 'border-purple-600', text: 'text-purple-300', icon: <Users className="h-4 w-4" /> },
  PAID: { bg: 'bg-blue-900/40', border: 'border-blue-600', text: 'text-blue-300', icon: <Zap className="h-4 w-4" /> },
  FREE: { bg: 'bg-gray-900/40', border: 'border-gray-600', text: 'text-gray-300', icon: <User className="h-4 w-4" /> },
};

const COIN_COLORS = {
  BTC: '#F7931A', ETH: '#627EEA', SOL: '#9945FF', LTC: '#345D9D',
  DOGE: '#C2A633', BCH: '#8DC351', XRP: '#23292F', SHIB: '#FF2D00',
  USD: '#00FF41'
};

// Smart price formatter - shows enough decimals for low-value coins
const fmtPrice = (price) => {
  if (!price || price === 0) return '$0.00';
  if (price >= 1) return `$${price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  if (price >= 0.01) return `$${price.toFixed(4)}`;
  if (price >= 0.0001) return `$${price.toFixed(6)}`;
  return `$${price.toFixed(8)}`;
};

const fmtValue = (val) => {
  if (!val || val === 0) return '$0.00';
  if (Math.abs(val) >= 1) return `$${val.toFixed(2)}`;
  if (Math.abs(val) >= 0.01) return `$${val.toFixed(4)}`;
  return `$${val.toFixed(6)}`;
};

const StatBox = ({ label, value, sub, icon, color = 'text-[#00FF41]' }) => (
  <div className="bg-[#0a0a0a] border border-[#003B00] p-3 text-center">
    <div className="flex items-center justify-center gap-1 mb-1 text-gray-500 text-xs uppercase tracking-wider">
      {icon} {label}
    </div>
    <div className={`text-lg font-bold font-mono ${color}`} data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>{value}</div>
    {sub && <div className="text-xs text-gray-600 mt-0.5">{sub}</div>}
  </div>
);

const PieChart = ({ holdings, totalValue }) => {
  const entries = Object.entries(holdings).filter(([, v]) => v.usd_value > 0);
  if (entries.length === 0) return <div className="text-gray-600 text-sm text-center py-8">No holdings found — portfolio may be empty or prices unavailable</div>;

  let cumAngle = 0;
  const slices = entries.map(([coin, data]) => {
    const pct = totalValue > 0 ? data.usd_value / totalValue : 0;
    const startAngle = cumAngle;
    cumAngle += pct * 360;
    return { coin, pct, startAngle, endAngle: cumAngle, color: COIN_COLORS[coin] || '#444', usd: data.usd_value };
  });

  const toRad = (deg) => (deg - 90) * (Math.PI / 180);
  const cx = 100, cy = 100, r = 80;

  return (
    <div className="flex items-center gap-6">
      <svg viewBox="0 0 200 200" className="w-44 h-44 flex-shrink-0">
        {slices.map((s, i) => {
          if (s.pct >= 0.999) return <circle key={i} cx={cx} cy={cy} r={r} fill={s.color} />;
          const largeArc = s.pct > 0.5 ? 1 : 0;
          const x1 = cx + r * Math.cos(toRad(s.startAngle));
          const y1 = cy + r * Math.sin(toRad(s.startAngle));
          const x2 = cx + r * Math.cos(toRad(s.endAngle));
          const y2 = cy + r * Math.sin(toRad(s.endAngle));
          return <path key={i} d={`M${cx},${cy} L${x1},${y1} A${r},${r} 0 ${largeArc},1 ${x2},${y2} Z`} fill={s.color} stroke="#000" strokeWidth="1" />;
        })}
        <circle cx={cx} cy={cy} r={40} fill="#0a0a0a" />
        <text x={cx} y={cy - 6} textAnchor="middle" fill="#00FF41" fontSize="12" fontFamily="monospace">${totalValue.toLocaleString()}</text>
        <text x={cx} y={cy + 10} textAnchor="middle" fill="#666" fontSize="8" fontFamily="monospace">TOTAL</text>
      </svg>
      <div className="space-y-1.5 flex-1 min-w-0">
        {slices.map((s) => (
          <div key={s.coin} className="flex items-center gap-2 text-xs">
            <div className="w-3 h-3 rounded-sm flex-shrink-0" style={{ backgroundColor: s.color }} />
            <span className="text-gray-300 font-mono w-10">{s.coin}</span>
            <div className="flex-1 bg-[#001a00] h-2 rounded-full overflow-hidden">
              <div className="h-full rounded-full" style={{ width: `${s.pct * 100}%`, backgroundColor: s.color }} />
            </div>
            <span className="text-gray-400 font-mono w-16 text-right">{fmtValue(s.usd)}</span>
            <span className="text-gray-600 font-mono w-10 text-right">{(s.pct * 100).toFixed(1)}%</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const TradeCard = ({ trade, label, isGood }) => {
  if (!trade) return <div className="text-gray-600 text-sm text-center py-4">No trades yet</div>;
  const profit = trade.profit || 0;
  const usdVal = trade.usd_value || 0;
  return (
    <div className={`p-3 border ${isGood ? 'border-green-800 bg-green-950/20' : 'border-red-800 bg-red-950/20'}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-gray-500 uppercase">{label}</span>
        <Badge className={`text-xs ${isGood ? 'bg-green-900 text-green-300' : 'bg-red-900 text-red-300'}`}>
          P/L: {profit >= 0 ? '+' : ''}{profit.toFixed(4)} USD
        </Badge>
      </div>
      <div className="flex items-center gap-4 text-xs text-gray-400">
        <span className="font-mono font-bold text-[#00FF41]">{trade.pair}</span>
        <span>Value: {fmtValue(usdVal)}</span>
        <span className="text-gray-600">@ {fmtPrice(trade.price)}</span>
        {trade.trigger && <Badge className="bg-[#001a00] text-[#00FF41] text-[10px]">{trade.trigger}</Badge>}
      </div>
      {trade.timestamp && <div className="text-[10px] text-gray-600 mt-1">{new Date(trade.timestamp).toLocaleString()}</div>}
    </div>
  );
};

const ConfigBadge = ({ enabled, label }) => (
  <div className="flex items-center gap-1.5 text-xs">
    {enabled ? <CheckCircle className="h-3 w-3 text-green-500" /> : <XCircle className="h-3 w-3 text-gray-600" />}
    <span className={enabled ? 'text-green-400' : 'text-gray-600'}>{label}</span>
  </div>
);

const BADGE_ICONS = {
  // Positive
  'zap': <Zap className="h-4 w-4" />,
  'activity': <Activity className="h-4 w-4" />,
  'trending-up': <TrendingUp className="h-4 w-4" />,
  'award': <Award className="h-4 w-4" />,
  'cpu': <Cpu className="h-4 w-4" />,
  'dollar-sign': <DollarSign className="h-4 w-4" />,
  'banknote': <Banknote className="h-4 w-4" />,
  'gem': <Gem className="h-4 w-4" />,
  'flame': <Flame className="h-4 w-4" />,
  // Negative
  'scissors': <Scissors className="h-4 w-4" />,
  'trending-down': <TrendingDown className="h-4 w-4" />,
  'arrow-down-circle': <ArrowDownCircle className="h-4 w-4" />,
  'shopping-bag': <ShoppingBag className="h-4 w-4" />,
  'skull': <Skull className="h-4 w-4" />,
  'thumbs-down': <ThumbsDown className="h-4 w-4" />,
  'flag': <Flag className="h-4 w-4" />,
  'frown': <Frown className="h-4 w-4" />,
  'alert-triangle': <AlertTriangle className="h-4 w-4" />,
  'anchor': <Anchor className="h-4 w-4" />,
  'factory': <Factory className="h-4 w-4" />,
  // Fun
  'moon': <Moon className="h-4 w-4" />,
  'sunrise': <Sunrise className="h-4 w-4" />,
  'dog': <Dog className="h-4 w-4" />,
  'heart': <Heart className="h-4 w-4" />,
  'layers': <Layers className="h-4 w-4" />,
  'repeat': <Repeat className="h-4 w-4" />,
};

const BADGE_CATEGORY_COLORS = {
  trades: { bg: 'bg-blue-900/40', border: 'border-blue-700', text: 'text-blue-300' },
  profit: { bg: 'bg-green-900/40', border: 'border-green-700', text: 'text-green-300' },
  streak: { bg: 'bg-orange-900/40', border: 'border-orange-700', text: 'text-orange-300' },
  special: { bg: 'bg-purple-900/40', border: 'border-purple-700', text: 'text-purple-300' },
  negative: { bg: 'bg-red-950/60', border: 'border-red-600', text: 'text-red-400' },
  fun: { bg: 'bg-amber-900/40', border: 'border-amber-700', text: 'text-amber-300' },
};

const BadgeCard = ({ badge }) => {
  const colors = BADGE_CATEGORY_COLORS[badge.category] || BADGE_CATEGORY_COLORS.trades;
  const isNegative = badge.category === 'negative';
  return (
    <div
      className={`${colors.bg} ${colors.border} border p-2.5 flex items-center gap-2.5 transition-all hover:scale-[1.02] ${isNegative ? 'border-dashed shadow-[0_0_8px_rgba(220,38,38,0.3)]' : ''}`}
      data-testid={`badge-${badge.id}`}
    >
      <div className={`${colors.text} flex-shrink-0 ${isNegative ? 'animate-pulse' : ''}`}>
        {BADGE_ICONS[badge.icon] || <Award className="h-4 w-4" />}
      </div>
      <div className="min-w-0">
        <div className={`text-xs font-bold ${colors.text} truncate`}>{badge.name}</div>
        <div className="text-[10px] text-gray-500 truncate">{badge.desc}</div>
      </div>
    </div>
  );
};

const BadgesSection = ({ badges, totalDefs }) => {
  const hasSim = badges.simulation && badges.simulation.length > 0;
  const hasLive = badges.live && badges.live.length > 0;
  if (!hasSim && !hasLive) return null;

  const groupBadges = (badgeList) => {
    const positive = badgeList.filter(b => ['trades', 'profit', 'streak', 'special'].includes(b.category));
    const negative = badgeList.filter(b => b.category === 'negative');
    const fun = badgeList.filter(b => b.category === 'fun');
    return { positive, negative, fun };
  };

  const renderBadgeGroup = (label, badgeList, labelClass) => {
    if (!badgeList.length) return null;
    return (
      <div className="space-y-2">
        <p className={`text-[10px] uppercase tracking-wider font-bold ${labelClass}`}>{label}</p>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
          {badgeList.map(b => <BadgeCard key={b.id} badge={b} />)}
        </div>
      </div>
    );
  };

  const renderMode = (label, badgeList, badgeClass) => {
    const { positive, negative, fun } = groupBadges(badgeList);
    return (
      <div className="space-y-3">
        <div className="flex items-center gap-2 mb-1">
          <Badge className={`${badgeClass} text-[10px]`}>{label}</Badge>
          <span className="text-[10px] text-gray-600">{badgeList.length} badges</span>
        </div>
        {renderBadgeGroup('Achievements', positive, 'text-green-600')}
        {renderBadgeGroup('Wall of Shame', negative, 'text-red-500')}
        {renderBadgeGroup('Fun & Quirky', fun, 'text-amber-500')}
      </div>
    );
  };

  return (
    <Card className="bg-[#0a0a0a] border-[#003B00]" data-testid="badges-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm text-[#00FF41] flex items-center gap-2">
          <Trophy className="h-4 w-4" /> PERFORMANCE BADGES
          <span className="text-xs text-gray-600 ml-auto font-normal">
            {(badges.simulation?.length || 0) + (badges.live?.length || 0)} / {totalDefs * 2} earned
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        {hasSim && renderMode('SIMULATION', badges.simulation, 'bg-blue-900/50 text-blue-300 border border-blue-700')}
        {hasLive && renderMode('LIVE', badges.live, 'bg-red-900/50 text-red-300 border border-red-700')}
      </CardContent>
    </Card>
  );
};

const UserProfilePage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [digestPrefs, setDigestPrefs] = useState({ daily_enabled: true, weekly_enabled: true });
  const [exporting, setExporting] = useState(false);
  const [highScoreBadges, setHighScoreBadges] = useState([]);

  const fetchProfile = useCallback(async () => {
    try {
      const res = await axios.get(`${API_V2}/user/profile/${userId}`, { withCredentials: true });
      setData(res.data);
      // Fetch digest prefs if viewing own profile
      if (res.data.user?.is_self) {
        try {
          const dp = await axios.get(`${API_V2}/user/digest/preferences`, { withCredentials: true });
          if (dp.data.preferences) setDigestPrefs(dp.data.preferences);
        } catch {}
      }
      // Fetch high score badges for this user
      try {
        const lb = await axios.get(`${API_V2}/leaderboard/stats`, { withCredentials: true });
        const d = lb.data;
        const uid = res.data.user?.user_id;
        const held = [];
        const badgeChecks = [
          { key: 'best_trade', stat: d.best_trade },
          { key: 'worst_trade', stat: d.worst_trade },
          { key: 'most_active_24h', stat: d.most_active_24h },
          { key: 'least_active_24h', stat: d.least_active_24h },
          { key: 'profit_king', stat: d.profit_king },
          { key: 'win_streak', stat: d.win_streak },
          { key: 'diversifier', stat: d.diversifier },
          { key: 'night_owl', stat: d.night_owl },
          { key: 'early_bird', stat: d.early_bird },
          { key: 'comeback_kid', stat: d.comeback_kid },
          { key: 'whale', stat: d.whale },
          { key: 'penny_pincher', stat: d.penny_pincher },
          { key: 'marathon_runner', stat: d.marathon_runner },
          { key: 'paper_hands', stat: d.paper_hands },
          { key: 'hodler', stat: d.hodler },
        ];
        for (const { key, stat } of badgeChecks) {
          if (stat && stat.user_id === uid) {
            held.push({ key, ...stat });
          }
        }
        setHighScoreBadges(held);
      } catch {}
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { fetchProfile(); }, [fetchProfile]);

  // Auto-refresh every 15 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      fetchProfile();
    }, 15000);
    return () => clearInterval(interval);
  }, [fetchProfile]);

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API_V2}/user/trades/export-csv`, {
        withCredentials: true,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `bitbot_trades_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Trade history exported');
    } catch {
      toast.error('Failed to export trades');
    } finally {
      setExporting(false);
    }
  };

  const toggleDigestPref = async (key) => {
    const updated = { ...digestPrefs, [key]: !digestPrefs[key] };
    setDigestPrefs(updated);
    try {
      await axios.put(`${API_V2}/user/digest/preferences`, updated, { withCredentials: true });
      toast.success('Email preferences updated');
    } catch {
      setDigestPrefs(digestPrefs);
      toast.error('Failed to update preferences');
    }
  };

  if (loading) return <div className="min-h-screen bg-black flex items-center justify-center"><Loader2 className="h-8 w-8 text-[#00FF41] animate-spin" /></div>;
  if (error) return <div className="min-h-screen bg-black flex items-center justify-center text-red-500">{error}</div>;
  if (!data) return null;

  const { user, performance: perf, holdings, total_portfolio_value, config, recent_trades, badges } = data;
  const tier = TIER_STYLES[user.access_level] || TIER_STYLES.FREE;
  const memberSince = user.created_at ? new Date(user.created_at) : null;
  const daysSinceJoin = memberSince ? Math.floor((Date.now() - memberSince.getTime()) / 86400000) : null;

  return (
    <div className="min-h-screen bg-black text-[#00FF41]">
      <AppBranding />
      <div className="p-4">
      <div className="max-w-5xl mx-auto space-y-4">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate(-1)} className="text-[#00FF41] hover:bg-[#003B00]" data-testid="profile-back-btn">
            <ArrowLeft className="h-4 w-4 mr-2" /> Back
          </Button>
          <h1 className="text-xl font-bold tracking-wider">USER PROFILE</h1>
          {user.is_self && <Badge className="bg-[#003B00] text-[#00FF41] text-xs">YOUR PROFILE</Badge>}
          <div className="ml-auto flex items-center gap-2">
            {user.is_self && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportCSV}
                disabled={exporting}
                className="text-[#00FF41] border-[#003B00] hover:bg-[#003B00] text-xs"
                data-testid="export-csv-btn"
              >
                <Download className="h-3 w-3 mr-1.5" />
                {exporting ? 'Exporting...' : 'Export CSV'}
              </Button>
            )}
          </div>
        </div>

        {/* User Identity Card */}
        <Card className={`bg-[#0a0a0a] ${tier.border}`} data-testid="user-identity-card">
          <CardContent className="p-5">
            <div className="flex items-center gap-5">
              {user.picture ? (
                <img src={user.picture} alt={user.name} className="h-16 w-16 rounded-full border-2" style={{ borderColor: tier.border.replace('border-', '') }} />
              ) : (
                <div className="h-16 w-16 rounded-full bg-[#001500] border-2 border-[#003B00] flex items-center justify-center">
                  <User className="h-8 w-8 text-[#008F11]" />
                </div>
              )}
              <div className="flex-1">
                <div className="flex items-center gap-3">
                  <h2 className="text-2xl font-bold text-[#00FF41]" data-testid="profile-username">{user.name || 'Unknown'}</h2>
                  <Badge className={`${tier.bg} ${tier.text} ${tier.border} border flex items-center gap-1`} data-testid="profile-access-level">
                    {tier.icon} {user.access_level}
                  </Badge>
                </div>
                <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
                  {user.email && <span>{user.email}</span>}
                  {memberSince && (
                    <span className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" /> Joined {memberSince.toLocaleDateString()}
                      {daysSinceJoin !== null && <span className="text-gray-600">({daysSinceJoin}d ago)</span>}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3 mt-2">
                  <Badge className="bg-[#001a00] text-[#00FF41] border border-[#003B00] text-xs">{config.mode?.toUpperCase()}</Badge>
                  <Badge className="bg-[#001a00] text-[#008F11] border border-[#003B00] text-xs">{config.strategy}</Badge>
                  <span className="text-xs text-gray-600">Pairs: {config.active_pairs?.join(', ')}</span>
                </div>
              </div>
              <div className="text-right">
                <div className="text-xs text-gray-500 uppercase">Portfolio Value</div>
                <div className="text-2xl font-bold font-mono text-[#00FF41]" data-testid="profile-portfolio-value">
                  ${total_portfolio_value.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* High Score Badges */}
        {highScoreBadges.length > 0 && (
          <Card className="bg-[#0a0a0a] border-yellow-600/50" data-testid="high-score-badges-section">
            <CardHeader className="pb-2 border-b border-yellow-600/20">
              <CardTitle className="text-sm text-yellow-400 flex items-center gap-2">
                <Trophy className="h-4 w-4" /> HIGH SCORE TROPHIES
                <Badge className="bg-yellow-900/50 text-yellow-300 text-[10px] ml-auto">{highScoreBadges.length} HELD</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-3">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {highScoreBadges.map((b) => {
                  const color = b.badge?.color || '#FFD700';
                  return (
                    <div
                      key={b.key}
                      className="relative p-3 rounded-lg border"
                      style={{ borderColor: color, background: `${color}0A` }}
                      data-testid={`hs-badge-${b.key}`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <div className="w-6 h-6 rounded-full flex items-center justify-center" style={{ background: `${color}22` }}>
                          <Trophy className="h-3 w-3" style={{ color }} />
                        </div>
                        <span className="text-xs font-bold truncate" style={{ color }}>{b.badge?.name}</span>
                      </div>
                      <p className="text-[10px] text-[#008F11] leading-tight">{b.badge?.description}</p>
                      {/* Stat value */}
                      <div className="mt-2 text-sm font-bold" style={{ color }}>
                        {b.key === 'best_trade' && `+$${Math.abs(b.profit || 0).toFixed(2)}`}
                        {b.key === 'worst_trade' && `-$${Math.abs(b.loss || 0).toFixed(2)}`}
                        {b.key === 'most_active_24h' && `${b.trade_count} trades`}
                        {b.key === 'least_active_24h' && `${b.trade_count} trades`}
                        {b.key === 'profit_king' && `+$${(b.total_profit || 0).toFixed(2)}`}
                        {b.key === 'win_streak' && `${b.streak} wins`}
                        {b.key === 'diversifier' && `${b.coin_count} coins`}
                        {b.key === 'night_owl' && `${b.trade_count} trades`}
                        {b.key === 'early_bird' && `${b.trade_count} trades`}
                        {b.key === 'comeback_kid' && `+$${b.recovery_amount}`}
                        {b.key === 'whale' && `$${(b.volume || 0).toLocaleString()}`}
                        {b.key === 'penny_pincher' && `${b.roi_pct}% ROI`}
                        {b.key === 'marathon_runner' && `${b.duration_hours}h`}
                        {b.key === 'paper_hands' && b.hold_display}
                        {b.key === 'hodler' && b.hold_display}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Key Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2" data-testid="stats-grid">
          <StatBox label="Total Trades" value={perf.total_trades} sub={`${perf.total_buys}B / ${perf.total_sells}S`} icon={<Activity className="h-3 w-3" />} />
          <StatBox label="Win Rate" value={`${perf.win_rate}%`} sub={`${perf.winning_trades}W / ${perf.losing_trades}L`} icon={<Target className="h-3 w-3" />} color={perf.win_rate >= 50 ? 'text-green-400' : 'text-red-400'} />
          <StatBox label="All-Time P/L" value={`${perf.total_profit >= 0 ? '+' : ''}$${perf.total_profit.toFixed(4)}`} icon={<DollarSign className="h-3 w-3" />} color={perf.total_profit >= 0 ? 'text-green-400' : 'text-red-400'} />
          <StatBox label="Avg/Trade" value={`$${perf.avg_profit_per_trade.toFixed(6)}`} icon={<BarChart3 className="h-3 w-3" />} color={perf.avg_profit_per_trade >= 0 ? 'text-green-400' : 'text-red-400'} />
          <StatBox label="Best Streak" value={`${perf.max_win_streak}W`} sub={`${perf.max_lose_streak}L max`} icon={<Flame className="h-3 w-3" />} color="text-yellow-400" />
          <StatBox label="Lifetime P/L" value={`$${(perf.lifetime_profit || 0).toFixed(2)}`} icon={<Trophy className="h-3 w-3" />} color={perf.lifetime_profit >= 0 ? 'text-green-400' : 'text-red-400'} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Best & Worst Trade */}
          <Card className="bg-[#0a0a0a] border-[#003B00]" data-testid="best-worst-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-[#00FF41] flex items-center gap-2"><Award className="h-4 w-4" /> BEST & WORST TRADES</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <TradeCard trade={perf.best_trade} label="Best Trade" isGood={true} />
              <TradeCard trade={perf.worst_trade} label="Worst Trade" isGood={false} />
            </CardContent>
          </Card>

          {/* Holdings Pie Chart */}
          <Card className="bg-[#0a0a0a] border-[#003B00]" data-testid="holdings-pie-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-[#00FF41] flex items-center gap-2"><BarChart3 className="h-4 w-4" /> CURRENT HOLDINGS</CardTitle>
            </CardHeader>
            <CardContent>
              <PieChart holdings={holdings} totalValue={total_portfolio_value} />
            </CardContent>
          </Card>
        </div>

        {/* Profit by Pair */}
        {Object.keys(perf.profit_by_pair).length > 0 && (
          <Card className="bg-[#0a0a0a] border-[#003B00]" data-testid="profit-by-pair-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-[#00FF41] flex items-center gap-2"><TrendingUp className="h-4 w-4" /> PROFIT BY PAIR</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {Object.entries(perf.profit_by_pair).map(([pair, profit]) => (
                  <div key={pair} className="flex items-center justify-between p-2 border border-[#003B00]/50 text-xs">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full" style={{ backgroundColor: COIN_COLORS[pair.split('-')[0]] || '#444' }} />
                      <span className="font-mono text-gray-400">{pair}</span>
                      {perf.trades_by_pair[pair] && <span className="text-gray-600">({perf.trades_by_pair[pair]})</span>}
                    </div>
                    <span className={`font-mono font-bold ${profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {profit >= 0 ? '+' : ''}{profit.toFixed(4)}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Daily P/L Chart */}
        {perf.profit_by_day.length > 0 && (
          <Card className="bg-[#0a0a0a] border-[#003B00]" data-testid="daily-pnl-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-[#00FF41] flex items-center gap-2"><Calendar className="h-4 w-4" /> DAILY P/L (LAST 30 DAYS)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-end gap-1 h-24">
                {perf.profit_by_day.map((d, i) => {
                  const maxAbs = Math.max(...perf.profit_by_day.map(x => Math.abs(x.profit)), 0.01);
                  const pct = Math.abs(d.profit) / maxAbs;
                  const h = Math.max(pct * 100, 4);
                  return (
                    <div key={i} className="flex-1 flex flex-col items-center justify-end h-full group relative" title={`${d.date}: $${d.profit.toFixed(4)}`}>
                      <div
                        className={`w-full min-w-[3px] rounded-t-sm ${d.profit >= 0 ? 'bg-green-500' : 'bg-red-500'}`}
                        style={{ height: `${h}%` }}
                      />
                      <div className="absolute -top-6 hidden group-hover:block bg-black border border-[#003B00] px-1 py-0.5 text-[9px] text-gray-400 whitespace-nowrap z-10">
                        {d.date}: ${d.profit.toFixed(4)}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="flex justify-between text-[9px] text-gray-700 mt-1">
                <span>{perf.profit_by_day[0]?.date}</span>
                <span>{perf.profit_by_day[perf.profit_by_day.length - 1]?.date}</span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Badges Section */}
        {badges && <BadgesSection badges={badges} totalDefs={badges.total_definitions || 13} />}

        {/* Email Digest Preferences (own profile only) */}
        {user.is_self && (
          <Card className="bg-[#0a0a0a] border-[#003B00]" data-testid="digest-prefs-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-[#00FF41] flex items-center gap-2"><Mail className="h-4 w-4" /> EMAIL REPORTS</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-6">
                <button
                  onClick={() => toggleDigestPref('daily_enabled')}
                  className="flex items-center gap-2 text-xs group"
                  data-testid="toggle-daily-digest"
                >
                  {digestPrefs.daily_enabled
                    ? <CheckCircle className="h-4 w-4 text-green-500" />
                    : <XCircle className="h-4 w-4 text-gray-600" />}
                  <span className={digestPrefs.daily_enabled ? 'text-green-400' : 'text-gray-600'}>
                    Daily P/L Digest
                  </span>
                </button>
                <button
                  onClick={() => toggleDigestPref('weekly_enabled')}
                  className="flex items-center gap-2 text-xs group"
                  data-testid="toggle-weekly-digest"
                >
                  {digestPrefs.weekly_enabled
                    ? <CheckCircle className="h-4 w-4 text-green-500" />
                    : <XCircle className="h-4 w-4 text-gray-600" />}
                  <span className={digestPrefs.weekly_enabled ? 'text-green-400' : 'text-gray-600'}>
                    Weekly Report
                  </span>
                </button>
                <span className="text-[10px] text-gray-600 ml-auto">Daily at 6:30 AM UTC | Weekly on Mondays</span>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Config Settings */}
          <Card className="bg-[#0a0a0a] border-[#003B00]" data-testid="config-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-[#00FF41] flex items-center gap-2"><Settings className="h-4 w-4" /> TRADING CONFIG</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="p-2 border border-[#003B00]/50">
                  <span className="text-gray-500">RSI Buy</span>
                  <span className="text-[#00FF41] font-mono ml-2">&le; {config.rsi_oversold}</span>
                </div>
                <div className="p-2 border border-[#003B00]/50">
                  <span className="text-gray-500">RSI Sell</span>
                  <span className="text-[#00FF41] font-mono ml-2">&ge; {config.rsi_overbought}</span>
                </div>
                <div className="p-2 border border-[#003B00]/50">
                  <span className="text-gray-500">Trade %</span>
                  <span className="text-[#00FF41] font-mono ml-2">{config.trade_percent}%</span>
                </div>
                <div className="p-2 border border-[#003B00]/50">
                  <span className="text-gray-500">Concentration</span>
                  <span className="text-[#00FF41] font-mono ml-2">{config.concentration_limit_percent}%</span>
                </div>
              </div>
              <Separator className="bg-[#003B00]/50" />
              <div className="grid grid-cols-2 gap-y-1.5 gap-x-4">
                <ConfigBadge enabled={config.stop_loss_enabled} label={`Stop Loss (${config.stop_loss_percent}%)`} />
                <ConfigBadge enabled={config.take_profit_enabled} label={`Take Profit (${config.take_profit_percent}%)`} />
                <ConfigBadge enabled={config.trailing_stop_enabled} label={`Trailing Stop (${config.trailing_stop_percent}%)`} />
                <ConfigBadge enabled={config.micro_profit_enabled} label={`Micro Profit (${config.micro_profit_percent}%)`} />
                <ConfigBadge enabled={config.no_loss_sell_enabled} label="No-Loss Sell Rule" />
                <ConfigBadge enabled={config.sell_cooldown_enabled} label={`Cooldown (${config.sell_cooldown_minutes}m)`} />
                <ConfigBadge enabled={config.concentration_limit_enabled} label="Concentration Limit" />
              </div>
            </CardContent>
          </Card>

          {/* Recent Trades */}
          <Card className="bg-[#0a0a0a] border-[#003B00]" data-testid="recent-trades-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-[#00FF41] flex items-center gap-2"><Clock className="h-4 w-4" /> RECENT TRADES</CardTitle>
            </CardHeader>
            <CardContent>
              {/* Column Headers */}
              <div className="flex items-center justify-between text-[10px] text-gray-600 uppercase tracking-wider px-1.5 pb-1.5 border-b border-[#003B00]/50 mb-1">
                <div className="flex items-center gap-2 w-24">
                  <span className="w-8">Type</span>
                  <span>Pair</span>
                </div>
                <span className="w-20 text-right">Value</span>
                <span className="w-20 text-right">P/L | Price</span>
                <span className="w-20 text-right">Date</span>
              </div>
              <div className="space-y-0 max-h-[300px] overflow-y-auto pr-1">
                {recent_trades.length === 0 ? (
                  <div className="text-gray-600 text-sm text-center py-4">No trades yet</div>
                ) : recent_trades.map((t, i) => {
                  const usdVal = t.usd_value || (Math.abs((t.amount || 0) * (t.price || 0)));
                  return (
                    <div key={i} className="flex items-center justify-between text-xs p-1.5 border-b border-[#003B00]/30">
                      <div className="flex items-center gap-2 w-24">
                        <Badge className={`text-[10px] px-1 py-0 w-8 text-center ${t.type === 'BUY' ? 'bg-green-900 text-green-300' : 'bg-red-900 text-red-300'}`}>
                          {t.type}
                        </Badge>
                        <span className="font-mono text-gray-400">{t.pair}</span>
                      </div>
                      <span className="font-mono text-gray-400 w-20 text-right">{fmtValue(usdVal)}</span>
                      <span className={`font-mono w-20 text-right ${!t.profit || t.profit === 0 ? 'text-gray-600' : t.profit > 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {t.type === 'SELL' && t.profit !== undefined && t.profit !== 0
                          ? `${t.profit >= 0 ? '+' : ''}${t.profit.toFixed(4)}`
                          : t.type === 'BUY' && t.price ? fmtPrice(t.price) : '-'}
                      </span>
                      <span className="text-gray-700 w-20 text-right">{t.timestamp ? new Date(t.timestamp).toLocaleDateString() : ''}</span>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Timeline Info */}
        <Card className="bg-[#0a0a0a] border-[#003B00]" data-testid="timeline-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between text-xs text-gray-500">
              <div className="flex items-center gap-4">
                {perf.first_trade_time && (
                  <span>First trade: {new Date(perf.first_trade_time).toLocaleDateString()}</span>
                )}
                {perf.last_trade_time && (
                  <span>Last trade: {new Date(perf.last_trade_time).toLocaleDateString()}</span>
                )}
              </div>
              <span>User ID: {user.user_id}</span>
            </div>
          </CardContent>
        </Card>
      </div>
      </div>
    </div>
  );
};

export default UserProfilePage;
